import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Document, DocumentType } from '../types/document';
import { EntityType } from '../types/changeRequest';
import { documentService } from '../services/documents';
import { formatAthensDate } from '../utils/athensTime';
import LoadingSpinner from '../components/common/LoadingSpinner';
import FileUpload from '../components/common/FileUpload';
import FormField from '../components/common/FormField';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { UserRole } from '../types/auth';
import { Container, Stack, Grid, Flex } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { StatusIndicator } from '../design-system/patterns/StatusIndicator';
import { PageHeader } from '../design-system/patterns/PageHeader';
import { Icon } from '../components/common/Icon';
import { iconMappings } from '../utils/iconMappings';
import { EmptyState } from '../components/common/EmptyState';
import SearchBar from '../components/common/SearchBar';
import { GroupedList } from '../components/common/GroupedList';
import { GroupingSelector, GroupingOption } from '../components/common/GroupingSelector';
import { groupByCategory, groupByDate, useGroupLabels } from '../utils/grouping';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { animations } from '../design-system/tokens/animations';
import { shadows } from '../design-system/tokens/shadows';
import { breakpoints } from '../design-system/tokens/breakpoints';

const DocumentsContainer = styled(Container)`
  max-width: 1200px;
`;

const UploadSection = styled(Card)`
  padding: ${spacing[8]};
`;

const DocumentsList = styled(Card)`
  padding: ${spacing[8]};
`;

const DocumentItem = styled(Card)`
  padding: ${spacing[4]};
  margin-bottom: ${spacing[3]};
  cursor: pointer;
  transition: ${animations.transition(['box-shadow', 'border-color'], animations.duration.normal, animations.easing.easeOut)};
  &:hover {
    box-shadow: ${props => shadows.colored.md(props.theme.colors.primary)};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const DocumentIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borders?.radius?.md || '8px'};
  background: ${props => props.theme.colors.surfaceHover};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: ${spacing[3]};
`;

const Filters = styled(Flex)`
  margin-bottom: ${spacing[6]};
`;

const TextEllipsis = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FlexWrap = styled(Flex)`
  flex-wrap: wrap;
`;

const DocumentRow = styled(Flex)`
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};

  > *:first-child {
    flex: 1 1 12rem;
    min-width: 0;
  }

  > *:last-child {
    flex: 0 0 auto;
  }

  ${breakpoints.down.md} {
    > *:last-child {
      width: 100%;
      justify-content: stretch;

      & > * {
        flex: 1;
      }
    }
  }
`;

const Documents: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const { success: showSuccess, error: showError } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<DocumentType>(DocumentType.Other);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [grouping, setGrouping] = useState<string>('none');
  const { getCategoryLabel, getDateLabel } = useGroupLabels();

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [documents, typeFilter]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await documentService.getMyDocuments();
      setDocuments(docs);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('documents.loadError', { defaultValue: 'Failed to load documents' }));
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = documents;
    
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(doc => doc.documentType === typeFilter);
    }
    
    // Search filter
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.fileName.toLowerCase().includes(searchLower) ||
        doc.documentType.toString().toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredDocuments(filtered);
  };

  // Grouping options
  const groupingOptions: GroupingOption[] = [
    { value: 'none', label: t('grouping.none') },
    { value: 'type', label: t('grouping.byCategory') },
    { value: 'date', label: t('grouping.byDate') },
  ];

  const handleUpload = async () => {
    if (!docFile || !user) return;

    // Determine entity type and ID based on user role
    let entityType: EntityType;
    let entityId: string;

    // For now, we'll need to get the member/institution ID
    // This is a simplified approach - in production, you'd fetch the actual entity ID
    if (user.role === UserRole.IndividualMember) {
      entityType = EntityType.IndividualMember;
      // We'd need to fetch the member ID - for now using user ID as placeholder
      entityId = user.id;
    } else {
      entityType = EntityType.Institution;
      entityId = user.id;
    }

    setUploading(true);
    try {
      await documentService.uploadDocument({
        file: docFile,
        documentType: selectedDocType,
        entityType,
        entityId,
      });
      showSuccess(t('documents.uploadSuccess', { defaultValue: 'Document uploaded successfully' }));
      setDocFile(null);
      await loadDocuments();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('documents.uploadError', { defaultValue: 'Failed to upload document' }));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId: string) => {
    try {
      const blob = await documentService.downloadDocument(docId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documents.find(d => d.id === docId)?.originalFileName || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('documents.downloadError', { defaultValue: 'Failed to download document' }));
    }
  };

  const handleDelete = async () => {
    if (!docToDelete) return;
    try {
      await documentService.deleteDocument(docToDelete);
      showSuccess(t('documents.deleteSuccess', { defaultValue: 'Document deleted successfully' }));
      setShowDeleteDialog(false);
      setDocToDelete(null);
      await loadDocuments();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('documents.deleteError', { defaultValue: 'Failed to delete document' }));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    return formatAthensDate(dateString, i18n.language);
  };

  const getDocumentTypeLabel = (type: DocumentType): string => {
    const keys: Partial<Record<DocumentType, string>> = {
      [DocumentType.IdentityDocument]: 'documents.types.identity',
      [DocumentType.ProofOfAddress]: 'documents.types.proofOfAddress',
      [DocumentType.ProfessionalCertificate]: 'documents.types.professionalCertificate',
      [DocumentType.FoundationAct]: 'documents.types.foundationAct',
      [DocumentType.Other]: 'documents.types.other',
      [DocumentType.CV]: 'documents.types.cv',
      [DocumentType.ProfessionalActivityDocuments]: 'documents.types.professionalActivity',
      [DocumentType.StudentID]: 'documents.types.studentId',
    };
    return t(keys[type] || 'documents.types.other');
  };

  const getDocumentTypeIcon = (type: DocumentType) => {
    const iconMap: Partial<Record<DocumentType, any>> = {
      [DocumentType.IdentityDocument]: iconMappings.document,
      [DocumentType.ProofOfAddress]: iconMappings.documentText,
      [DocumentType.ProfessionalCertificate]: iconMappings.documentCheck,
      [DocumentType.FoundationAct]: iconMappings.documentText,
      [DocumentType.CV]: iconMappings.documentText,
      [DocumentType.ProfessionalActivityDocuments]: iconMappings.documentText,
      [DocumentType.StudentID]: iconMappings.document,
      [DocumentType.Other]: iconMappings.document,
    };
    return iconMap[type] || iconMappings.document;
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <DocumentsContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('documents.title')}
          pattern={true}
        />

        <UploadSection variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
          <Stack direction="column" spacing="lg">
            <Heading variant="h3" color="text">
              {t('documents.uploadDocument')}
            </Heading>
        <FormField
          label={t('documents.documentType')}
          select
          options={[
            { value: DocumentType.IdentityDocument.toString(), label: t('documents.types.identity') },
            { value: DocumentType.ProofOfAddress.toString(), label: t('documents.types.proofOfAddress') },
            { value: DocumentType.ProfessionalCertificate.toString(), label: t('documents.types.professionalCertificate') },
            { value: DocumentType.FoundationAct.toString(), label: t('documents.types.foundationAct') },
            { value: DocumentType.Other.toString(), label: t('documents.types.other') },
          ]}
          value={selectedDocType.toString()}
          onChange={(e) => setSelectedDocType(parseInt(e.target.value) as DocumentType)}
          disabled={uploading}
        />
        <FileUpload
          onFileSelect={setDocFile}
          onFileRemove={() => setDocFile(null)}
          currentFile={docFile}
          accept=".pdf,.jpg,.jpeg,.png"
          maxSize={10 * 1024 * 1024}
          label={t('documents.selectFile')}
          hint={t('documents.uploadHint')}
        />
            {docFile && (
              <Button
                onClick={handleUpload}
                variant="gradient"
                glow
                ripple
                loading={uploading}
              >
                {t('documents.upload')}
              </Button>
            )}
          </Stack>
        </UploadSection>

        <DocumentsList variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
          <Stack direction="column" spacing="lg">
            <Heading variant="h3" color="text">
              {t('documents.myDocuments')}
            </Heading>
            <Filters direction="row" gap="md">
          <FormField
            label={t('documents.filterByType')}
            select
            options={[
              { value: 'all', label: t('documents.allTypes') },
              { value: DocumentType.IdentityDocument.toString(), label: t('documents.types.identity') },
              { value: DocumentType.ProofOfAddress.toString(), label: t('documents.types.proofOfAddress') },
              { value: DocumentType.ProfessionalCertificate.toString(), label: t('documents.types.professionalCertificate') },
              { value: DocumentType.FoundationAct.toString(), label: t('documents.types.foundationAct') },
              { value: DocumentType.Other.toString(), label: t('documents.types.other') },
            ]}
            value={typeFilter === 'all' ? 'all' : typeFilter.toString()}
            onChange={(e) => setTypeFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as DocumentType)}
          />
        </Filters>
            {filteredDocuments.length === 0 ? (
              <EmptyState
                icon="document"
                title={t('documents.noDocuments')}
                text={t('documents.noDocumentsText')}
              />
            ) : grouping !== 'none' ? (
              <GroupedList
                items={filteredDocuments}
                groupBy={(doc) => {
                  if (grouping === 'type') {
                    return doc.documentType.toString();
                  }
                  if (grouping === 'date') {
                    const date = new Date(doc.uploadedAt);
                    const now = new Date();
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const thisWeek = new Date(today);
                    thisWeek.setDate(today.getDate() - 7);
                    const thisMonth = new Date(today);
                    thisMonth.setMonth(today.getMonth() - 1);
                    
                    if (date >= today) return 'today';
                    if (date >= thisWeek) return 'thisWeek';
                    if (date >= thisMonth) return 'thisMonth';
                    return 'older';
                  }
                  return 'other';
                }}
                groupLabel={(key) => {
                  if (grouping === 'type') {
                    return getDocumentTypeLabel(parseInt(key) as DocumentType);
                  }
                  if (grouping === 'date') {
                    return getDateLabel(key);
                  }
                  return key;
                }}
                renderItem={(doc) => (
                  <DocumentItem key={doc.id} variant="filled" hoverElevation accentBorder="left">
                    <DocumentRow direction="row" gap="md">
                      <Stack direction="column" spacing="sm">
                        <TextEllipsis size="sm" weight="semibold">
                          {doc.originalFileName}
                        </TextEllipsis>
                        <FlexWrap direction="row" align="center" gap="sm">
                          <Text size="xs" color="secondary">{getDocumentTypeLabel(doc.documentType)}</Text>
                          <Text size="xs" color="secondary">•</Text>
                          <Text size="xs" color="secondary">{formatFileSize(doc.fileSize)}</Text>
                          <Text size="xs" color="secondary">•</Text>
                          <Text size="xs" color="secondary">{formatDate(doc.uploadedAt)}</Text>
                          {doc.isVerified && (
                            <>
                              <Text size="xs" color="secondary">•</Text>
                              <StatusIndicator status="success" size="sm" label={t('documents.verified')} />
                            </>
                          )}
                        </FlexWrap>
                      </Stack>
                      <Flex direction="row" gap="sm">
                        <Button variant="outline" size="sm" onClick={() => handleDownload(doc.id)}>
                          {t('documents.download')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setDocToDelete(doc.id);
                            setShowDeleteDialog(true);
                          }}
                        >
                          {t('documents.delete')}
                        </Button>
                      </Flex>
                    </DocumentRow>
                  </DocumentItem>
                )}
                collapsible
                defaultExpanded={[]}
              />
            ) : (
              <Stack direction="column" spacing="sm">
                {filteredDocuments.map((doc) => (
                  <DocumentItem key={doc.id} variant="filled" hoverElevation accentBorder="left">
                    <DocumentRow direction="row" gap="md">
                      <Stack direction="column" spacing="sm">
                        <TextEllipsis size="sm" weight="semibold">
                          {doc.originalFileName}
                        </TextEllipsis>
                        <FlexWrap direction="row" align="center" gap="sm">
                          <Text size="xs" color="secondary">{getDocumentTypeLabel(doc.documentType)}</Text>
                          <Text size="xs" color="secondary">•</Text>
                          <Text size="xs" color="secondary">{formatFileSize(doc.fileSize)}</Text>
                          <Text size="xs" color="secondary">•</Text>
                          <Text size="xs" color="secondary">{formatDate(doc.uploadedAt)}</Text>
                          {doc.isVerified && (
                            <>
                              <Text size="xs" color="secondary">•</Text>
                              <StatusIndicator status="success" size="sm" label={t('documents.verified')} />
                            </>
                          )}
                        </FlexWrap>
                      </Stack>
                      <Flex direction="row" gap="sm">
                        <Button variant="outline" size="sm" onClick={() => handleDownload(doc.id)}>
                          {t('documents.download')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setDocToDelete(doc.id);
                            setShowDeleteDialog(true);
                          }}
                        >
                          {t('documents.delete')}
                        </Button>
                      </Flex>
                    </DocumentRow>
                  </DocumentItem>
                ))}
              </Stack>
            )}
          </Stack>
          </DocumentsList>
      </Stack>
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDocToDelete(null);
        }}
        onConfirm={handleDelete}
        title={t('documents.deleteDocument')}
        message={t('documents.deleteConfirm')}
        confirmText={t('documents.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
      />
    </DocumentsContainer>
  );
};

export default Documents;
